<template>
  <div class="title-block">
    <span v-if="data.length > 1">{{ $t("app.modals.moveParticipant.subtitle.bulk", { count: data.length }) }}</span>
    <i18n-t tag="span" keypath="app.modals.moveParticipant.subtitle.solo" v-else>
      <template #talent>
        <span class="red-color bold" v-safe-html="candidateWording" />
      </template>
    </i18n-t>
  </div>
  <p class="under-text">
    <i18n-t tag="span" keypath="app.modals.moveParticipant.text1" :plural="data.length">
      <template #campaign>
        <span class="bold">{{ currentCampaign.campaign.internal_name }}</span>
      </template>
    </i18n-t>
    {{ $t("app.modals.moveParticipant.text2", { count: data.length }) }}
  </p>
  <div class="input-wrapper">
    <LabeledSelect class="sm" id="campaign-space" :placeholder="$t('app.modals.moveParticipant.workspace.placeholder')" name="campaign-space" option-label="name" :label="$t('app.modals.moveParticipant.workspace.label')" v-model="state.space" :clearable="false" :options="isSpacesPending ? [] : spaces">
      <template v-slot:no-options="{ search, searching }">
        <div style="padding: 8px 0">{{ $t("app.modals.moveParticipant.noResult", { search }) }}</div>
      </template>
    </LabeledSelect>
  </div>
  <div class="input-wrapper">
    <LabeledSelect class="sm" id="campaign-space" :placeholder="$t('app.modals.moveParticipant.campaign.placeholder')" :label="$t('app.modals.moveParticipant.campaign.label')" name="campaign-space" :get-option-label="(opt) => opt.campaign.internal_name" v-model="state.campaign" :disabled="!state.space" :clearable="false" :options="spaceCampaigns?.filter((obj) => obj.id !== currentCampaign.id) || []">
      <template v-slot:no-options="{ search, searching }">
        <div style="padding: 8px 0">{{ $t("app.modals.moveParticipant.noResult", { search }) }}</div>
      </template>
    </LabeledSelect>
  </div>
  <div class="btn-container">
    <Button class="secondary outline" :label="$t('app.modals.moveParticipant.buttons.cancel')" @click="emits('close')" />
    <Button class="primary" :loading="state.btnIsLoading" :disabled="!state.campaign" :label="$t('app.modals.moveParticipant.buttons.confirm', { count: data.length })" @click="moveParticipants" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch, inject } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useQuery } from "@tanstack/vue-query";

import ToastContent from "@/components/global/ToastContent.vue";

import { campaignsApi } from "@/api/campaigns.api";

import { useSpaces } from "@/queries/space.query";
import { useCampaign } from "@/queries/campaigns.query";
import { useMoveParticipations } from "@/queries/participant.query";
import LabeledSelect from "@/components/global/LabeledSelect.vue";

const toast = useToast();
const route = useRoute();
const { t } = useI18n();

const { mutateAsync: moveParticipations } = useMoveParticipations();

const emits = defineEmits(["close", "confirm", "confirmBulk"]);

const state = reactive({
  space: null,
  campaign: null,
  btnIsLoading: false,
});

const { data: spaces, isPending: isSpacesPending } = useSpaces();

const { data: spaceCampaigns, isPending: isSpaceCampaignsPending } = useQuery({
  queryKey: ["campaigns", route.params.companyId, computed(() => state?.space?.workspace_id)],
  queryFn: () => campaignsApi.getAll(route.params.companyId as string, state?.space?.workspace_id, false),
  enabled: computed(() => !!state?.space?.workspace_id),
})

const { data: currentCampaign, isPending } = useCampaign()

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
});


const candidateWording = computed(() => {
  let candidate;

  if (props.data[0].candidate_id) {
    candidate = `${props.data[0].candidate.first_name} ${props.data[0].candidate.last_name}`;
  } else if (props.data[0].pending_first_name && props.data[0].pending_last_name) {
    candidate = `${props.data[0].pending_first_name} ${props.data[0].pending_last_name}`;
  } else {
    candidate = props.data[0].pending_email;
  }

  return candidate;
});

async function moveParticipants() {
  state.btnIsLoading = true;

  await moveParticipations({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string,
      campaignId: route.params.campaignId as string,
    },
    participantIds: props.data.map((elt) => elt.participant_id),
    destinationCampaignId: state.campaign.id,
  },
  {
    onError: (error) => {
      toast.error(error);
    },
    onSuccess: () => { 
      const url = `/organisation/${route.params.companyId as string}/spaces/${state.campaign.campaign.parent_workspace_id}/campaigns/${state.campaign.id}`;

      toast.success({
        component: ToastContent,
        props: {
          text: t("app.modals.moveParticipant.toasts.success", { url, campaignName: state.campaign.campaign.internal_name }),
        },
        closeOnClick: false,
      });

      if (props.data.length > 1) emits("confirmBulk");
    }
  });

  emits("close");

  state.btnIsLoading = true;
}

</script>
